import clsx from 'clsx';
import React, { useEffect } from 'react';
import StatusCodes from 'http-status-codes';
import { FlexBox, Text, withErrorHandling } from '@is24at/react-ui-core';
import { ArrowRight, Location, Phone } from '@is24at/react-ui-core/Icons';
import { Http404, Http500 } from '@is24at/static-pages';
import {
  Image,
  ArtDimension,
  AspectRatio,
  getDimensions,
  imageProps,
} from '@is24at/react-image';
import { OVILogo } from 'assets/OVILogo';
import { IRLogo } from 'assets/IRLogo';
import MainLayout from 'layouts/MainLayout';
import { scrollToTop } from 'utils/utils';
import { TestId } from 'utils/constants';
import { REALTOR_PROFILE_PRO_FEATURE } from 'utils/enums';
import { BackButton } from 'components/BackButton';
import { ShareButton } from 'components/ShareButton';
import { AwardImages } from 'components/Awards';
import PropertyList from '../RealtorProperties';
import FormContainer from '../FormContainer';
import NotificationContainer from '../NotificationContainer';
import { RealEstateAgentStructuredData } from '../RealEstateAgentStructuredData';
import styles from './ExposeView.scss';

const SCROLL_TO_TOP_DURATION = 1;

const LogoSizeSet = {
  GLOBAL: getDimensions(144, 48, AspectRatio.APS_P),
} as ImageSizeSet;

const ProRealtorSizeSet = {
  GLOBAL: { width: 700, height: 500 },
} as ImageSizeSet;

const ExposeHeaderPro = ({ companyName, proRealtorImageUrl, logoUrl }) => (
  <div
    className={styles['expose-header--pro']}
    data-testid={TestId.EXPOSE_HEADER_PRO}
  >
    <Image
      pictureCn={styles['expose-agency-realtor-pro__picture']}
      imageCn={styles['expose-agency-realtor-pro__image']}
      alt={`Makler ${companyName}`}
      {...imageProps(proRealtorImageUrl, ProRealtorSizeSet)}
    />

    {logoUrl && (
      <Image
        pictureCn={styles['expose-agency-logo__picture--pro']}
        imageCn={styles['expose-agency-logo__image--pro']}
        alt={`Makler ${companyName} logo`}
        {...imageProps(logoUrl, LogoSizeSet)}
      />
    )}
  </div>
);

const ExposeHeaderBasic = ({ companyName, logoUrl }) => (
  <div
    className={styles['expose-header--basic']}
    data-testid={TestId.EXPOSE_HEADER_BASIC}
  >
    {logoUrl && (
      <Image
        pictureCn={styles['expose-agency-logo__picture--basic']}
        imageCn={styles['expose-agency-logo__image--basic']}
        alt={`Makler ${companyName} logo`}
        {...imageProps(logoUrl, LogoSizeSet, {
          resize: ArtDimension.HEIGHT,
        })}
      />
    )}
  </div>
);

type Props = {
  expose: {
    companyName: string;
    addressLines: Array<string>;
    phoneNumbers?: Array<string>;
    logoUrl?: string;
    badges?: string[];
    website?: string;
    features?: Array<string>;
    proRealtorImageUrl?: string;
    proRealtorAboutUsText?: string;
    streetString?: string;
    city?: string;
    countryCode?: string;
    zipCode?: string;
    exposePath?: string;
  };
  baseUrl: string;
};

const ExposeView = ({ expose, baseUrl }: Props) => {
  const {
    companyName,
    phoneNumbers,
    addressLines,
    logoUrl,
    badges,
    website,
    features = [],
    proRealtorImageUrl,
    proRealtorAboutUsText,
  } = expose;

  const hasRealtorProFeature = features.includes(REALTOR_PROFILE_PRO_FEATURE);
  const ovi = badges?.includes('OVI');
  const ir = badges?.includes('IMMOBILIENRING');

  useEffect(() => {
    scrollToTop(SCROLL_TO_TOP_DURATION);
  }, []);

  return (
    <MainLayout>
      <RealEstateAgentStructuredData {...expose} baseUrl={baseUrl} />
      <FlexBox
        className={styles['expose-box']}
        direction="column"
        spacing={0}
        alignItems="left"
      >
        <FlexBox
          className={styles['expose-toolbar']}
          justifyContent="space-between"
        >
          <BackButton />
          <ShareButton companyName={companyName} />
        </FlexBox>
        <NotificationContainer />
        {hasRealtorProFeature && proRealtorImageUrl ? (
          <ExposeHeaderPro {...{ proRealtorImageUrl, logoUrl, companyName }} />
        ) : (
          <ExposeHeaderBasic {...{ logoUrl, companyName }} />
        )}
        <Text
          className={clsx(styles['expose-heading'], 'mt-24 mb-32')}
          tag="h1"
          bold
        >
          {companyName}
        </Text>
        {hasRealtorProFeature && proRealtorAboutUsText && (
          <>
            <Text size="l" className="mb-16" bold>
              Über uns
            </Text>
            <div
              className={clsx(styles['expose-about-us'], 'mb-16')}
              dangerouslySetInnerHTML={{ __html: proRealtorAboutUsText }}
            />
          </>
        )}
        <AwardImages companyName={companyName} activeFeatures={features} />
        {(ir || ovi) && (
          <>
            <Text size="l" className="mb-16" bold>
              Mitgliedschaften von {companyName}
            </Text>
            <FlexBox spacing={0} className="mb-24">
              {ir && (
                <IRLogo
                  width={32}
                  height={28}
                  aria-label="immobilienring logo"
                />
              )}
              {ovi && <OVILogo width={47} height={25} aria-label="övi logo" />}
            </FlexBox>
          </>
        )}
        {hasRealtorProFeature && <FormContainer />}
        <Text size="l" className="mb-16 mt-24" bold>
          Kontakt
        </Text>
        {!!addressLines?.length && (
          <FlexBox spacing={12} className="mb-16">
            <Location size={20} className={styles['location-icon']} />
            <FlexBox spacing={0} direction="column" alignItems="left">
              {addressLines.map((line: any) => (
                <Text key={line}>{line}</Text>
              ))}
            </FlexBox>
          </FlexBox>
        )}
        {!!phoneNumbers?.length && (
          <>
            {phoneNumbers.map((phoneNumber: any) => (
              <FlexBox spacing={12} key={phoneNumber}>
                <a
                  href={`tel:${phoneNumber}`}
                  className={styles['center-icon']}
                >
                  <Phone color="blue" size={20} className="mr-12" />
                  <Text color="blue">{phoneNumber}</Text>
                </a>
              </FlexBox>
            ))}
          </>
        )}
        {website && (
          <FlexBox spacing={12}>
            <a
              href={website}
              target="_blank"
              rel="noreferrer"
              className={styles['center-icon']}
            >
              <ArrowRight color="blue" size={20} className="mr-12" />
              <Text color="blue">{website}</Text>
            </a>
          </FlexBox>
        )}
        <PropertyList />
      </FlexBox>
    </MainLayout>
  );
};

export default withErrorHandling(ExposeView, {
  [StatusCodes.BAD_REQUEST]: Http404,
  [StatusCodes.NOT_FOUND]: Http404,
  [StatusCodes.INTERNAL_SERVER_ERROR]: Http500,
  [StatusCodes.BAD_GATEWAY]: Http500,
});
